import type { IconRadioContentProps } from "@/components/forms/IconRadio.tsx";
import type { VmPasswordInputs } from "@/modules/compute/PasswordFields.tsx";
import type { VmSizePackage } from "@/modules/compute/VmSize.types.ts";
import {
    calculateSizeInputs,
    forceSizeForOs,
    getDefaultSize,
    getSizeParams,
    onSizeChange,
    type SizeInputs,
    type SizeViewModel,
} from "@/modules/compute/commonViewModel.ts";
import {
    type ComputeImageType,
    getDefaultOs,
    getResourceTypeImages,
    type SelectedOs,
} from "@/modules/compute/os/os.ts";
import { getDefaultDesignatedPoolForLocation } from "@/modules/compute/pool/pool.ts";
import type { VmSizeParams } from "@/modules/compute/vmSizeSelectUtils.ts";
import type { LocationInputs } from "@/modules/location/LocationField.tsx";
import type { LocationsForType } from "@/modules/location/location.ts";
import type { LocationSlug } from "@/modules/location/locationQuery.ts";
import type * as hostPoolsQuery from "@/modules/pools/hostPoolsQuery.ts";
import type { HostPoolWithType } from "@/modules/pools/hostPoolsQuery.ts";
import type { GetField, SetField } from "@/utils/getField.tsx";
import type { VmCreateBody, VmImage } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import type { ReactNode } from "react";

export interface ItemType extends IconRadioContentProps {
    id: ComputeImageType;
    description: ReactNode;
}

export interface VmCreateInputs
    // Optional fields
    extends
        Pick<
            VmCreateBody,
            | "network_uuid"
            | "source_uuid"
            | "designated_pool_uuid"
            | "source_replica"
            | "billing_account_id"
            | "cloud_init"
            | "boot_firmware"
        >,
        // Required fields
        Required<Pick<VmCreateBody, "name" | "username" | "public_keys" | "reserve_public_ip">>,
        VmPasswordInputs,
        LocationInputs,
        SizeInputs {
    typeId: ComputeImageType;
    // NB: Bundle the OS form control state into an object so it can be integrated nicely with react-hook-form's <Controller>
    os: SelectedOs;
}

export class VmCreateViewModel implements SizeViewModel {
    get: GetField<VmCreateInputs> = (_n) => undefined;
    set: SetField<VmCreateInputs> = (_n, _v) => {};
    _sizeParams: VmSizeParams;

    constructor(
        // Necessary source data & configuration to validate values
        private readonly types: ItemType[],
        private readonly allImages: VmImage[],
        private readonly locations: LocationsForType,
        private readonly pools: hostPoolsQuery.HostPoolWithType[],
        public readonly _packages: VmSizePackage[],
        private readonly _base: Partial<VmCreateInputs> | undefined,
    ) {
        this._sizeParams = getSizeParams();
    }

    attachGetterAndSetter(getter: typeof this.get, setter: typeof this.set) {
        this.get = getter;
        this.set = setter;
    }

    onChangeType = (newValue: ComputeImageType) => {
        console.debug("onChangeType, value: %o", newValue);
        this.onChangeOs(this.getDefaultOsForType(newValue));
        this.set("typeId", newValue);
    };

    get _os() {
        return this.get("os") ?? this._base?.os ?? this.getDefaultOs();
    }

    get _diskSize() {
        return this._base?.size?.disks;
    }

    onChangeOs = (newValue: SelectedOs) => {
        console.debug("onChangeOs, value: %o", newValue);
        this.set("os", newValue);

        // Forward to common handler
        forceSizeForOs.call(this);
    };

    onLocationChange = (newValue: string) => {
        console.debug("onLocationChange, value: %o", newValue);
        this.set("location", newValue);

        this.onDesignatedPoolChange(notNull(getDefaultDesignatedPoolForLocation(this.pools, newValue)?.uuid));

        this.set("network_uuid", undefined); // field selects its own default and sets it to form field
    };

    getSizeParams(): VmSizeParams {
        return this._sizeParams;
    }

    onPoolChange = (pool: HostPoolWithType) => {
        console.debug("onPoolChange, value: %o", pool);
        this._sizeParams = getSizeParams(pool.guest_limits);

        this.set("designated_pool_uuid", pool?.uuid);
        this.onSizeChange(getDefaultSize.bind(this)());
    };

    onDesignatedPoolChange = (newValue: string) => {
        console.debug("onDesignatedPoolChange, value: %o", newValue);
        const pool = this.pools.find((p) => p.uuid === newValue);
        this.onPoolChange(notNull(pool));
    };

    onSizeChange = onSizeChange.bind(this);

    //#region Default values
    private getDefaultType(): ComputeImageType {
        return this.types[0].id;
    }

    private getDefaultLocation(): LocationSlug {
        return this.locations.defaultLocation;
    }

    private getDefaultOsForType(type: ComputeImageType): SelectedOs {
        const imagesForType = getResourceTypeImages(this.allImages, type);
        return getDefaultOs(imagesForType);
    }

    private getDefaultOs(): SelectedOs {
        return this.getDefaultOsForType(this.getDefaultType());
    }

    private getDefaultDesignatedPoolUuid() {
        const pool = getDefaultDesignatedPoolForLocation(this.pools, this.getDefaultLocation());
        this.onPoolChange(notNull(pool));
        return pool?.uuid;
    }

    getDefaultValues(): VmCreateInputs {
        console.debug("Getting form default values");

        const base = this._base;
        return {
            name: "",
            public_keys: [],
            username: "",
            password: "",
            password_confirm: "",
            network_uuid: undefined,
            boot_firmware: "bios",

            ...base,

            typeId: base?.typeId ?? this.getDefaultType(),
            location: base?.location ?? this.getDefaultLocation(),
            reserve_public_ip: base?.reserve_public_ip ?? true,
            os: this._os,
            designated_pool_uuid: base?.designated_pool_uuid ?? this.getDefaultDesignatedPoolUuid(),
            ...calculateSizeInputs.call(this, base?.size),
        };
    }
    //#endregion Default values
}
